import { PageBanner } from 'components/common';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  Box,
  Flex,
  IconButton,
  reactIcons as Icons,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

const styles = {
  container: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  content: {
    width: '100%',
    minWidth: '645px',
  },
  headerColor: {
    position: 'relative',
    '&::before': {
      content: '',
      background: '$background-top-nav',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '160px',
      width: '100%',
      zIndex: '-1',
    },
  },
  panelButton: {
    bgColor: '$primary-top-nav',
    flex: 1,
    '&:hover': {
      bgColor: 'rgba(255,255,255,0.2)',
    },
  },
  panelLeft: {
    bgColor: '$background-top-nav',
    borderRight: '1px solid #415799',
    bottom: 0,
    flexShrink: 0,
    left: 0,
    top: 0,
    transitionDuration: '0.3s',
    width: '320px',
    zIndex: 99999,
  },
  panelRight: {
    bottom: 0,
    flexShrink: 0,
    right: 0,
    top: 0,
    transitionDuration: '0.3s',
    zIndex: 9999,
  },
  panelRightSub: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    padding: '0 0 $medium',
    transition: 'transform 0.3s',
  },
  panelUp: {
    flex: 1,
    left: 0,
    flexShrink: 0,
    right: 0,
    top: 0,
    transitionDuration: '0.3s',
    zIndex: 9999,
  },
};

interface IPageLayout {
  content: JSX.Element;
  disableBanner?: boolean;
  header?: JSX.Element;
  leftOpen?: boolean;
  leftPanel?: JSX.Element;
  rightOpen?: boolean;
  rightOpenTwo?: boolean;
  rightPanel?: JSX.Element[];
  setLeftOpen?: (next: boolean) => void;
  setUpOpen?: (next: boolean) => void;
  upOpen?: boolean;
}

function PageLayout({
  content,
  header,
  leftOpen,
  leftPanel,
  rightOpen,
  rightOpenTwo,
  rightPanel,
  setLeftOpen,
  upOpen = true,
  setUpOpen,
  disableBanner = false,
}: IPageLayout) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [rootWidth, setRootWidth] = useState(0);
  const [lastActive, setLastActive] = useState(0);

  useEffect(() => {
    if (rightOpenTwo) {
      setLastActive(1);
    } else if (rightOpen) {
      setLastActive(0);
    }
  }, [rightOpen, rightOpenTwo]);

  // TODO debounce
  useLayoutEffect(() => {
    const observeRoot = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      setRootWidth(entry.contentRect?.width || 0);
    });
    const observeContent = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      setContentWidth(entry.contentRect?.width || 0);
    });
    if (rootRef.current && contentRef.current) {
      observeRoot.observe(rootRef.current);
      observeContent.observe(contentRef.current);
    }
  }, []);

  return (
    <Flex ref={rootRef} css={styles.container}>
      {leftPanel && setLeftOpen && (
        <Flex
          css={{
            marginLeft: leftOpen ? '0' : '-280px',
            position: rootWidth < 1000 ? 'absolute' : 'relative',
            ...styles.panelLeft,
          }}
        >
          <Box css={{ flex: 1 }}>{leftPanel}</Box>
          <Flex orientation="vertical" gap="medium">
            {setUpOpen && (
              <IconButton
                onPress={() => setUpOpen(!upOpen)}
                children={upOpen ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                css={{ ...styles.panelButton, maxHeight: 80 }}
              />
            )}
            <IconButton
              aria-label={t('common.openLeftPanel')}
              onPress={() => setLeftOpen(!leftOpen)}
              children={leftOpen ? <Icons.Expanded /> : <Icons.Collapsed />}
              css={{
                ...styles.panelButton,
                position: leftOpen ? 'absolute' : '',
                right: leftOpen ? '$medium' : 0,
                top: leftOpen ? '$large' : 0,
              }}
            />
          </Flex>
        </Flex>
      )}
      <Flex
        data-testid="page-layout"
        orientation="vertical"
        css={{
          ...styles.content,
          marginLeft: !!leftPanel && rootWidth < 1000 ? '40px' : '',
        }}
      >
        {!disableBanner && <PageBanner />}
        <Box css={styles.headerColor}>
          <Flex
            orientation="vertical"
            css={{
              ...styles.panelUp,
              marginTop: upOpen ? '0' : '-90px',
              marginBottom: upOpen ? '0' : '$medium',
            }}
          >
            {header}
          </Flex>
        </Box>
        <Flex ref={contentRef} css={styles.container}>
          {content}
          {!!rightPanel && !!rightPanel.length && (
            <Flex
              css={{
                ...styles.panelRight,
                marginRight: rightOpen || rightOpenTwo ? '0' : '-645px',
                position: contentWidth < 1000 ? 'absolute' : '',
              }}
            >
              <Flex css={{ width: 645, position: 'relative' }}>
                <Flex
                  children={rightPanel[0]}
                  css={{
                    ...styles.panelRightSub,
                    transform:
                      rightOpen || lastActive === 0 ? '' : 'translateX(100%)',
                    zIndex: rightOpen || lastActive === 0 ? 1 : '',
                  }}
                />
                <Flex
                  children={rightPanel[1]}
                  css={{
                    ...styles.panelRightSub,
                    transform:
                      rightOpenTwo || lastActive === 1
                        ? ''
                        : 'translateX(100%)',
                  }}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}

export default PageLayout;
